import { evaluateContrast, type ContrastPairKey, type ContrastRow } from "./contrast";
import type { CmsEditor, CMSEditorConfig } from "./editor";
import { carryBookingPreviewState, isBookingPreviewParam, readBookingPreviewParam, withBookingPreviewParam } from "./booking-preview-url";
import { dispatchToast, getCSRFToken, processResponseTriggers, toastFetchError } from "../shared/htmx-helpers";

// The brand pairs whose suggested fix updates primary_color, and those that
// update accent_color — the mapping the Apply button uses to reach the right
// ColorField. Covers the CTA pairs plus every brand-vs-surface pair (…_background
// / …_surface / …_sectionalt / accent_primary). The neutral text pairs AND the
// surface_background elevation pair have no suggestion (ambiguous which of two
// neutrals to move) so they never appear here.
const BRAND_PAIR_FIELD: Partial<Record<ContrastPairKey, string>> = {
  cta_primary: "primary_color",
  primary_background: "primary_color",
  primary_surface: "primary_color",
  primary_sectionalt: "primary_color",
  cta_accent: "accent_color",
  accent_background: "accent_color",
  accent_surface: "accent_color",
  accent_sectionalt: "accent_color",
  accent_primary: "accent_color",
};

// hintKeyFor picks the plain-language severity word. The 3.0-target pairs (every
// brand-vs-surface pair + the surface↔background elevation pair) are about a
// color being too faint to SEE against what's behind it. The 4.5-target pairs
// (neutral text + CTA button text) are about legibility — unreadable (error)
// vs. large-text-only (warning).
function hintKeyFor(row: ContrastRow): "illegible" | "large_ok" | "faint" {
  if (row.target < 4.5) return "faint";
  return row.severity === "error" ? "illegible" : "large_ok";
}

// ── Contrast-chip tooltip (CHANGE 3, vanilla — deliberately NO Alpine) ──
// The section-title pill ("⚠ N") is a plain <button>. Its bubble is a MODULE-
// LEVEL SINGLETON teleported to document.body (not a sibling of the pill), so it
// escapes the editing pane's stacking/overflow context — which previously
// clipped the lg:absolute sibling BEHIND the left section-switcher rail. This
// mirrors the codebase Tooltip component (which teleports via x-teleport for
// exactly this clipping reason) but stays vanilla. The bubble is position:fixed
// and JS-positioned from the pill's viewport rect; it opens on click (tap-
// friendly for touch) AND, on pointer devices, on hover. Document-level dismiss
// listeners (click-outside + Escape + scroll + resize) live at MODULE scope,
// bound EXACTLY ONCE, so the many _updateContrastWarnings re-renders (every
// edit, every rail nav) never stack duplicate listeners. Avoiding Alpine here
// sidesteps both the post-swap hydration timing race the surrounding code
// already fights and the pending @alpinejs/csp migration.
let _contrastChipDocBound = false;

// The one contrast tooltip bubble, lazily created and parked on document.body.
let _contrastChipTip: HTMLElement | null = null;

// ensureChipTip lazily creates the singleton bubble, appends it to
// document.body, and returns it. Carries the bubble's VISUAL classes only — the
// positioning utilities (lg:absolute/max-lg:fixed and friends) are gone; layout
// is JS-driven via position:fixed set here. An explicit numeric zIndex (110 ==
// the z-popover token, the top of the scale) is pinned inline because a class
// on a body-teleported node can't be relied on to win over the app's stacking
// contexts; the desktop-docked section-switcher rail is z-auto, so 110 clears
// it (and header 40 / overlay 45 / modal 100) with room to spare.
function ensureChipTip(): HTMLElement {
  if (_contrastChipTip && _contrastChipTip.isConnected) return _contrastChipTip;
  const tip = document.createElement("div");
  tip.id = "cms-contrast-chip-tip";
  tip.setAttribute("role", "tooltip");
  tip.setAttribute("data-cms-contrast-chip-tip", "");
  tip.className =
    "hidden z-popover bg-neutral-900 text-neutral-50 text-xs rounded-field shadow-xl backdrop-blur-sm border border-white/10 py-1.5 px-3 leading-relaxed whitespace-normal font-medium tracking-wide";
  tip.style.position = "fixed";
  tip.style.zIndex = "110";
  document.body.appendChild(tip);
  _contrastChipTip = tip;
  return tip;
}

// openChipTip shows the singleton with `text` and positions it under `pillBtn`
// from the pill's live viewport rect (position:fixed).
function openChipTip(pillBtn: HTMLElement, text: string) {
  const tip = ensureChipTip();
  tip.textContent = text;
  tip.classList.remove("hidden");
  pillBtn.setAttribute("aria-expanded", "true");
  const rect = pillBtn.getBoundingClientRect();
  if (window.matchMedia("(max-width: 1023px)").matches) {
    // Mobile: full-width clamp with 8px gutters (preserves today's good look).
    tip.style.left = "8px";
    tip.style.right = "8px";
    tip.style.width = "auto";
    tip.style.maxWidth = "none";
    tip.style.top = rect.bottom + 6 + "px";
  } else {
    // Desktop: undo the mobile stretch, cap width, drop below the pill, then
    // align the bubble's RIGHT edge under the pill's right edge — clamped into
    // the viewport (8px gutters). offsetWidth is only meaningful AFTER the node
    // is visible and max-width is applied, so measure here.
    tip.style.right = "";
    tip.style.width = "";
    tip.style.maxWidth = "20rem";
    tip.style.top = rect.bottom + 6 + "px";
    const w = tip.offsetWidth;
    const left = Math.min(Math.max(rect.right - w, 8), window.innerWidth - w - 8);
    tip.style.left = left + "px";
  }
}

// closeContrastChipTips hides the singleton bubble and resets every pill's
// aria-expanded. Query-all keeps it robust even if _contrastChipTip is stale.
function closeContrastChipTips() {
  document.querySelectorAll<HTMLElement>("[data-cms-contrast-chip-tip]").forEach((tip) => {
    tip.classList.add("hidden");
  });
  document.querySelectorAll<HTMLElement>("[data-cms-contrast-chip-btn]").forEach((btn) => {
    btn.setAttribute("aria-expanded", "false");
  });
}

function bindContrastChipDocListeners() {
  if (_contrastChipDocBound) return;
  _contrastChipDocBound = true;
  // Click-outside: any click NOT on the pill closes the bubble. A click on the
  // pill is inside [data-cms-contrast-chip], so the button's own toggle handler
  // owns it and this listener leaves it alone. The bubble now lives in body (not
  // inside the chip) and has no interactive content, so a click on it correctly
  // dismisses — nothing to keep open.
  document.addEventListener("click", (e) => {
    const t = e.target as Element | null;
    if (t && t.closest("[data-cms-contrast-chip]")) return;
    closeContrastChipTips();
  });
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") closeContrastChipTips();
  });
  // The bubble is position:fixed with coords computed once at open; a scroll or
  // resize invalidates them. Close on either. Scroll uses capture phase — scroll
  // doesn't bubble, so this catches the inner editing pane too.
  document.addEventListener("scroll", () => closeContrastChipTips(), true);
  window.addEventListener("resize", () => closeContrastChipTips());
}

// editorPreview owns the live preview iframe: the section/full-page render
// modes, the in-place fragment swap vs. cache-bust reload, live CSS-var theme
// patching, font injection, contrast warnings, and the scroll
// capture/restore machinery for the POI manage-mode hide/show. It captures
// `config` only to seed _currentPreviewUpdate at construction (the rest of its
// state is DOM/store state reached through `this`).

// hasToastTrigger: the response already carries a flash-message HX-Trigger
// (dispatched by processResponseTriggers) — don't toast a second time.
function hasToastTrigger(resp: Response): boolean {
  return ["HX-Trigger", "HX-Trigger-After-Swap", "HX-Trigger-After-Settle"].some((h) =>
    (resp.headers.get(h) || "").includes("flash-message"),
  );
}

export function editorPreview(config: CMSEditorConfig): Partial<CmsEditor> & ThisType<CmsEditor> {
  return {
    // _currentPreviewUpdate is the update mode for the CURRENT section. Seeded
    // from config.previewUpdate (the entry section's server-computed mode) and
    // updated on each rail nav from the clicked link's data-cms-preview-update
    // attr, so it's never frozen at the initial value after navigation.
    _currentPreviewUpdate: (config.previewUpdate || "") as string,
    _previewTimer: null as ReturnType<typeof setTimeout> | null,
    _recolorTimer: null as ReturnType<typeof setTimeout> | null,
    _darkReloadTimer: null as ReturnType<typeof setTimeout> | null,
    // True when a preview refresh was requested (autosave flush, discard,
    // version restore, …) while the preview pane was hidden — e.g. the POI
    // manage-mode toggle sets
    // x-show="!($store.cmsMobileTab.manageMode && section === 'discover')"
    // on the preview wrapper (cms_editor.templ), which display:none's the
    // iframe. A refresh that runs while hidden reads win.scrollY as 0 and/or
    // can't scrollTo a zero-layout iframe, so exiting manage-mode landed the
    // preview scrolled to the top. Set by _refreshPreview when it skips a
    // refresh; cleared by _refreshPreview when a refresh actually runs, and
    // by the reveal observer in init() right before it re-triggers one.
    _previewDirty: false,
    // Last VISIBLE preview scroll position, tracked continuously by a
    // CAPTURE-phase scroll listener on the iframe document (see _onPreviewLoad).
    // The preview scrolls an INNER element (the device-frame content container),
    // NOT the iframe window — and scroll events don't bubble, so a window-level
    // listener never sees it and window.scrollY stays 0. Capture phase catches
    // the inner element's scroll; we store that element + its scrollTop/Left so
    // _restorePreviewScroll can put it back after the manage-mode hide/show.
    _previewScroll: null as { el: Element; top: number; left: number } | null,
    // Guards the scroll-capture poll during the reveal→restore window. On reveal
    // the content re-expands to full height (scrollable) but sits transiently at
    // scrollTop 0; without this the poll would capture that 0 and clobber the
    // pre-hide position we're about to restore. The edit path exposes the race
    // because _updatePreviewInPlace awaits a fetch (~250ms), leaving the poll a
    // full tick to fire before restore runs. Set true by the reveal observer,
    // cleared by _restorePreviewScroll once the position is back (or on bail).
    _suppressScrollCapture: false,

    _iframe(): HTMLIFrameElement | null {
      // Optional-chained (T4 fix): a unit test that stubs out every OTHER
      // $root-touching method (e.g. _applyPreviewTheme) but calls
      // _onPreviewLoad directly has no $root at all — _consentPreviewWrap
      // (called unconditionally from _onPreviewLoad) must not throw there.
      return (
        ((this as any).$root as HTMLElement | undefined)?.querySelector("[data-cms-preview]") ??
        null
      );
    },

    _previewMode(): string {
      return this._main()?.dataset.cmsPreviewMode || "section";
    },
    _previewUpdate(): string {
      return (
        this._currentPreviewUpdate || (this._previewMode() === "full-page" ? "in-place" : "reload")
      );
    },
    // The original behavior: cache-bust the iframe src → full reload. Kept for
    // section mode + as a fallback when same-origin DOM access is unavailable.
    _refreshPreviewReload() {
      // Cache-bust THROUGH the store so the reload survives the next post-swap
      // initTree (a baked-URL :src would otherwise revert it). Read the store's
      // current URL (the live section), strip any old _t, append a fresh one.
      const urlStore = (this as any).$store?.cmsPreviewURL;
      const cur = (urlStore?.value as string) || this._iframe()?.src || "";
      if (!cur) return;
      const base = cur.replace(/([?&])_t=\d+/, "$1").replace(/[?&]$/, "");
      const sep = base.includes("?") ? "&" : "?";
      const next = `${base}${sep}_t=${String(Date.now())}`;
      if (urlStore) urlStore.value = next;
      else {
        const f = this._iframe();
        if (f) f.src = next;
      }
    },
    // Faithful CMS booking preview switcher (cms_booking_preview_bar.templ):
    // reads/writes ?tab= / ?property= on the live preview URL store; the iframe
    // :src reloads reactively. No navigation of the editor page.
    bookingPreviewParam(name: string, fallback: string): string {
      if (!isBookingPreviewParam(name)) return fallback;
      const cur = ((this as any).$store?.cmsPreviewURL?.value as string) || this._iframe()?.getAttribute("src") || "";
      return readBookingPreviewParam(cur, name) || fallback;
    },
    // The accommodation dropdown's value: the property the frame actually
    // previews — ?property= when it is one of `ids` (the linked allowlist),
    // else the render handler's fallback, the first.
    bookingPreviewPropertyValue(ids: readonly string[]): string {
      const cur = this.bookingPreviewParam("property", "");
      return ids.includes(cur) ? cur : (ids[0] ?? "");
    },
    setBookingPreviewParam(name: string, value: string): void {
      if (!isBookingPreviewParam(name)) return;
      const urlStore = (this as any).$store?.cmsPreviewURL;
      const cur = (urlStore?.value as string) || this._iframe()?.getAttribute("src") || "";
      if (!cur) return;
      const next = withBookingPreviewParam(cur, name, value);
      if (next === cur) return;
      this._beginPreviewLoad();
      if (urlStore) urlStore.value = next;
      else {
        const f = this._iframe();
        if (f) f.src = next;
      }
    },
    // "Refresh prices": drop the server-side preview cache for this website,
    // then cache-bust-reload the frame (same path as a section-mode edit).
    // bookingPreviewRefreshing disables the button while the POST is in
    // flight (re-clicks are ignored). Failures toast: the server's own
    // HX-Trigger toast or JSON message when it sent one, else errorMsg.
    async refreshBookingPreview(url: string, errorMsg?: string): Promise<void> {
      if (!url || this.bookingPreviewRefreshing) return;
      this.bookingPreviewRefreshing = true;
      let ok = false;
      try {
        const resp = await fetch(url, {
          method: "POST",
          credentials: "same-origin",
          headers: { "X-CSRF-Token": getCSRFToken() },
        });
        processResponseTriggers(resp);
        ok = resp.ok;
        if (!ok && !hasToastTrigger(resp) && !(await toastFetchError(resp)) && errorMsg) {
          dispatchToast(errorMsg, "error");
        }
      } catch {
        if (errorMsg) dispatchToast(errorMsg, "error");
      } finally {
        this.bookingPreviewRefreshing = false;
      }
      if (!ok) return;
      this._beginPreviewLoad();
      this._refreshPreviewReload();
    },
    // Seeds $store.cmsPreviewURL from init()'s baked config.previewURL. A
    // body-swap re-init of the SAME booking preview keeps the operator's
    // tab/property (carryBookingPreviewState) instead of resetting them. Arms
    // the loading overlay only when the URL actually changes (a same-URL
    // write fires no `load`); the first-ever init (empty store) relies on the
    // default previewLoading=true.
    _seedPreviewURLStore(url: string): void {
      const urlStore = (this as any).$store?.cmsPreviewURL;
      if (!urlStore) return;
      const cur = (urlStore.value as string) || "";
      // urlStore.section: the editor section the stored URL was set for — the
      // full-page booking URL carries no section=, so this is what tells a
      // same-section re-init (keep the picked tab) from a section change.
      const prevSection = typeof urlStore.section === "string" && urlStore.section ? urlStore.section : undefined;
      const next = carryBookingPreviewState(cur, url, prevSection, prevSection === undefined ? undefined : this.section);
      if (cur && cur !== next) this._beginPreviewLoad();
      urlStore.value = next;
      urlStore.section = this.section;
    },

    // _isBookingFullPage: the booking editor (always full page — the faithful
    // checkout). Its section rail switches the checkout TAB (the rail link's
    // preview URL) instead of scrolling to a section anchor.
    _isBookingFullPage(): boolean {
      return config.context === "booking" && this._previewMode() === "full-page";
    },
    // Full-page in-place update: fetch the body fragment and swap
    // #cms-preview-root, preserving scroll. Alpine re-hydrates the new subtree
    // in-frame. Falls back to a reload on any same-origin/fetch failure.
    async _updatePreviewInPlace() {
      const iframe = this._iframe();
      const win = iframe?.contentWindow;
      const root = iframe?.contentDocument?.getElementById("cms-preview-root");
      if (!iframe || !win || !root) {
        this._refreshPreviewReload();
        return;
      }
      const base = iframe.src.replace(/([?&])_t=\d+/, "$1").replace(/[?&]$/, "");
      const url = base + (base.includes("?") ? "&" : "?") + "fragment=1";
      try {
        const resp = await fetch(url, { credentials: "same-origin" });
        if (!resp.ok) return; // fault → leave existing content (no flash)
        root.innerHTML = await resp.text();
        // The innerHTML swap resets the iframe scroll to 0. Restore the tracked
        // position (the iframe's scrollingElement), retrying across frames until
        // the re-rendered content is tall enough to hold it.
        this._restorePreviewScroll();
      } catch {
        /* network blip → leave existing content; next edit retries */
      }
    },
    _patchPreviewTheme(field: string, value: string) {
      const iframe = this._iframe();
      const doc = iframe?.contentDocument;
      const root = doc?.documentElement;
      if (!root || !doc) return;
      const colorVar: Record<string, string> = {
        primary_color: "--color-primary",
        accent_color: "--color-accent",
        background_color: "--color-background",
        surface_color: "--color-surface",
        text_color: "--color-text",
        text_muted_color: "--color-text-muted",
        section_alt_color: "--color-section-alt",
      };
      // Keep in sync with studio-sites/src/lib/button-style.ts PRESETS (cross-repo dual-copy; no build-time guard).
      const BUTTON_PRESETS: Record<
        string,
        { radius: string; textTransform: string; letterSpacing: string; shadow: string }
      > = {
        elegant: {
          radius: "var(--radius-md)",
          textTransform: "uppercase",
          letterSpacing: "0.15em",
          shadow: "none",
        },
        rounded: {
          radius: "var(--radius-full)",
          textTransform: "uppercase",
          letterSpacing: "0.15em",
          shadow: "none",
        },
        minimal: {
          radius: "0px",
          textTransform: "uppercase",
          letterSpacing: "0.15em",
          shadow: "none",
        },
        soft: {
          radius: "var(--radius-full)",
          textTransform: "none",
          letterSpacing: "normal",
          shadow: "var(--shadow-sm)",
        },
        modern: {
          radius: "var(--radius-md)",
          textTransform: "none",
          letterSpacing: "normal",
          shadow: "none",
        },
      };
      if (field in colorVar) {
        // Empty value (e.g. section_alt reset to Auto) must REMOVE the inline
        // override, not set it to "" — an empty custom property is
        // guaranteed-invalid, which would blank `var(--color-section-alt)` (and
        // the alternating bands) instead of falling back to the global.css
        // color-mix default. removeProperty restores the stylesheet rule.
        const isBrand = field === "primary_color" || field === "accent_color";
        if (isBrand && (this as any).$store.cmsPreviewTheme.value === "dark") {
          // In dark, the server [data-theme="dark"] block owns the brand. An inline
          // light value would override it; instead DEBOUNCE a flush-then-reload so the
          // server re-emits the block with the freshly-lifted color AFTER the edit is
          // saved to the draft. A bare synchronous reload would fetch the draft BEFORE
          // the 600ms autosave flush → stale color (the M4 race). Narrow case: editing
          // brand colors while viewing dark.
          if (this._darkReloadTimer) clearTimeout(this._darkReloadTimer);
          this._darkReloadTimer = setTimeout(() => {
            void this.flushPending().then(() => this._refreshPreviewReload());
          }, 600);
        } else if (value) {
          root.style.setProperty(colorVar[field], value);
        } else {
          root.style.removeProperty(colorVar[field]);
        }
      } else if (field === "spacing_density") {
        root.style.setProperty("--spacing-density", value);
      } else if (field === "button_style") {
        const p = BUTTON_PRESETS[value] ?? BUTTON_PRESETS.elegant;
        root.style.setProperty("--btn-radius", p.radius);
        root.style.setProperty("--btn-text-transform", p.textTransform);
        root.style.setProperty("--btn-letter-spacing", p.letterSpacing);
        root.style.setProperty("--btn-shadow", p.shadow);
      } else if (field === "heading_scale") {
        const n = parseFloat(value);
        root.style.setProperty("--heading-scale", n > 0 ? String(n) : "1");
      } else if (field === "text_scale_base") {
        const n = parseFloat(value);
        root.style.setProperty("--text-scale-base", n > 0 ? String(n) : "1");
      } else if (field === "font_heading") {
        const safe = value.replace(/'/g, "\\'");
        root.style.setProperty("--font-heading", `'${safe}', serif`);
        this._injectPreviewFont(doc, value);
      } else if (field === "font_body") {
        const safe = value.replace(/'/g, "\\'");
        root.style.setProperty("--font-body", `'${safe}', sans-serif`);
        this._injectPreviewFont(doc, value);
      } else if (field === "alternate_sections") {
        if (value === "false") {
          root.setAttribute("data-alt-sections", "off");
        } else {
          root.removeAttribute("data-alt-sections");
        }
      } else if (field === "consent_banner_position") {
        // Live-patch the consent-banner SAMPLE (T4) — same theme-live, no-reload
        // path as every other Design field. A no-op when the sample isn't
        // present (off Design, or unavailable this session).
        this._applyConsentBannerPosition(value);
      }
      // Dark preview is driven by $store.cmsPreviewTheme via _applyPreviewTheme.
    },
    // _applyPreviewTheme flips data-theme on the preview iframe root. The server
    // preview frame emits [data-theme="dark"]{ --color-primary/accent } (lifted),
    // so the attribute alone colors the brand — no client lift, no stash, no race.
    // Idempotent: safe to call repeatedly / on every iframe (re)attach.
    _applyPreviewTheme(theme: "light" | "dark") {
      (this as any).$store.cmsPreviewTheme.value = theme;
      try {
        localStorage.setItem("cms_preview_theme", theme);
      } catch {
        /* private mode / quota — non-fatal */
      }
      const root = this._iframe()?.contentDocument?.documentElement;
      if (!root) return;
      if (theme === "dark") root.setAttribute("data-theme", "dark");
      else root.removeAttribute("data-theme");
      // Maps bake their basemap flavor from the live data-theme at boot; the
      // toolbar toggle sets the attribute DIRECTLY (it never goes through the
      // preview's preferences.ts, so no studio:appearance-changed fires). A
      // "Map style follows theme" map therefore wouldn't switch to its dark
      // basemap. Re-run the map boot via the same hook the color patcher uses
      // (recolorAllMaps re-derives the flavor from the now-updated data-theme).
      const win = this._iframe()?.contentWindow as
        (Window & { __studioPreviewRecolor?: () => void }) | null | undefined;
      win?.__studioPreviewRecolor?.();
    },
    // setPreviewTheme is the toolbar entry point.
    setPreviewTheme(theme: "light" | "dark") {
      this._applyPreviewTheme(theme);
    },
    // _onPreviewLoad re-asserts the chosen theme whenever the iframe (re)loads
    // (idempotent attribute set). Reads the store so it's swap/re-init-proof.
    _onPreviewLoad() {
      // The iframe finished (re)loading → clear the loading overlay and its
      // safety timer. Covers initial paint, CMS page switch, and section-mode
      // section switch (a full-page-mode section switch only scrolls, so
      // previewLoading was never set for it).
      this.previewLoading = false;
      if (this._previewLoadTimer) {
        clearTimeout(this._previewLoadTimer);
        this._previewLoadTimer = null;
      }
      this._applyPreviewTheme((this as any).$store.cmsPreviewTheme.value);
      // Preview scroll is tracked by the poll set up in init() (reads the live
      // iframe document each tick), which is robust to the iframe document being
      // replaced on reload — unlike a document-bound scroll listener here.

      // Consent-banner sample (T4): every (re)load recreates the iframe's DOM
      // from scratch — a full navigate (_refreshPreviewReload) OR the initial
      // load always emits the sample HIDDEN by default (handler_render_
      // preview.go), so re-assert the CURRENT client toggle state (default
      // false — see showConsentBannerPreview's doc comment) and the current
      // draft position. An in-place fragment swap (_updatePreviewInPlace)
      // never touches this node (it sits outside #cms-preview-root) and never
      // fires `load`, so it does not re-run this — the sample correctly
      // survives that swap untouched. No-op off Design / when unavailable.
      const wrap = this._consentPreviewWrap();
      if (wrap) {
        wrap.hidden = !this.showConsentBannerPreview;
        this._applyConsentBannerPosition(this._currentConsentBannerPosition());
      }
      this._syncConsentPreviewControl();
    },

    // _beginPreviewLoad arms the preview-pane overlay before the iframe
    // (re)navigates. The safety timeout force-clears it if `load` never fires
    // (iframe error, blocked cross-origin, or a same-URL assignment that is a
    // no-op navigation) so the overlay can never stick over a live preview.
    _beginPreviewLoad() {
      this.previewLoading = true;
      if (this._previewLoadTimer) clearTimeout(this._previewLoadTimer);
      this._previewLoadTimer = setTimeout(() => {
        this.previewLoading = false;
        this._previewLoadTimer = null;
      }, 20000);
    },
    // _restorePreviewScroll puts the preview back to the last VISIBLE position.
    // On reveal the iframe content is momentarily COLLAPSED to the frame height
    // (scrollHeight == clientHeight) and re-expands over a few frames, and an
    // in-place refresh swaps #cms-preview-root's innerHTML (resetting scroll to
    // 0). So retry across animation frames until the scroller can hold the
    // target, then set it INSTANTLY (the scroller uses scroll-behavior:smooth,
    // which would otherwise animate the jump). No-op until a position is tracked.
    _restorePreviewScroll() {
      // Always release the capture guard when we're done (success, bail, or
      // give-up), so a stuck flag can never permanently freeze the poll.
      const done = () => {
        this._suppressScrollCapture = false;
      };
      const s = this._previewScroll;
      if (!s || !s.el.isConnected || (s.top === 0 && s.left === 0)) {
        done();
        return;
      }
      let tries = 0;
      const apply = () => {
        if (!s.el.isConnected) {
          done();
          return;
        }
        if (s.el.scrollHeight > s.el.clientHeight + 4) {
          if (typeof s.el.scrollTo === "function")
            s.el.scrollTo({ top: s.top, left: s.left, behavior: "instant" as ScrollBehavior });
          else {
            s.el.scrollTop = s.top;
            s.el.scrollLeft = s.left;
          }
          done();
        } else if (tries++ < 40) {
          requestAnimationFrame(apply);
        } else {
          done();
        }
      };
      requestAnimationFrame(apply);
    },
    _injectPreviewFont(doc: Document, family: string) {
      if (!family) return;
      if (!doc.head) return;
      const id = "cms-font-" + family.replace(/[^a-z0-9]/gi, "-").toLowerCase();
      if (doc.getElementById(id)) return;
      const link = doc.createElement("link");
      link.id = id;
      link.rel = "stylesheet";
      link.href =
        "https://fonts.googleapis.com/css2?family=" +
        encodeURIComponent(family) +
        ":wght@400;500;600;700;800&display=swap";
      doc.head.appendChild(link);
    },
    // _updateContrastWarnings re-evaluates the WHOLE theme palette (neutral text
    // legibility + the brand/CTA/elevation pairs, a11y-03) from the live color
    // inputs and renders a non-blocking alert CARD — placed right after the color
    // group, before the typography fields (CHANGE 2) — plus a labeled tap-tooltip
    // pill next to the section title (CHANGE 3). It NEVER disables save and NEVER
    // alters a color on its own — only the operator's Apply click does that.
    // Self-clears (removes the box + the pill) when nothing fails; a no-op on
    // sections that have no color inputs.
    _updateContrastWarnings() {
      const main = this._main();
      if (!main) return;
      const read = (key: string): string => {
        const el = main.querySelector<HTMLInputElement>(`[data-cms-field="${key}"]`);
        return el?.value ?? "";
      };
      const sectionAlt = read("section_alt_color");
      const rows = evaluateContrast({
        text: read("text_color"),
        textMuted: read("text_muted_color"),
        background: read("background_color"),
        surface: read("surface_color"),
        primary: read("primary_color"),
        accent: read("accent_color"),
        // Only pass sectionAlt when explicitly set; derived (empty) is not
        // client-computable without evaluating the color-mix CSS function.
        ...(sectionAlt ? { sectionAlt } : {}),
      });

      const m = this._contrastMsgs;

      // Labeled contrast pill next to the Design section title (#cms-editor-form
      // h2) — a warning-triangle mark + the count ("⚠ 2"), red when any row is an
      // error else amber. It opens the body-teleported tooltip singleton on click
      // (tap-friendly) and on hover (desktop only) — CHANGE 3, vanilla, see the
      // module helpers above (ensureChipTip / openChipTip / close…). No
      // templ change: injected/updated/removed here. The leftpane (and its h2) is
      // re-rendered on rail nav, so a stale pill can't survive a section switch;
      // on same-section edits we rebuild the single existing pill in place.
      const title = main.querySelector<HTMLElement>("#cms-editor-form h2");
      if (title) {
        let chip = title.querySelector<HTMLElement>("[data-cms-contrast-chip]");
        // Dismiss any open bubble before rebuilding or removing its owning pill,
        // else it strands (stale position, orphaned pill) on a keyboard-only edit
        // or when contrast is fixed to zero rows.
        closeContrastChipTips();
        if (rows.length === 0) {
          chip?.remove();
        } else {
          if (!chip) {
            chip = document.createElement("span");
            chip.setAttribute("data-cms-contrast-chip", "");
            chip.className = "relative inline-flex align-middle ml-2";
            title.appendChild(chip);
          }
          const anyError = rows.some((r) => r.severity === "error");
          chip.innerHTML = ""; // rebuild the single pill's innards fresh

          const pillBtn = document.createElement("button");
          pillBtn.type = "button";
          pillBtn.setAttribute("data-cms-contrast-chip-btn", "");
          pillBtn.setAttribute("aria-expanded", "false");
          pillBtn.setAttribute("aria-controls", "cms-contrast-chip-tip");
          pillBtn.setAttribute("aria-label", m.chipTooltip || m.heading || "Check contrast");
          pillBtn.className =
            "inline-flex items-center gap-1 text-[0.65rem] font-semibold rounded-full px-1.5 py-0.5 cursor-pointer " +
            (anyError ? "bg-error/20 text-error" : "bg-warning/20 text-warning");
          const mark = document.createElement("span");
          mark.setAttribute("aria-hidden", "true");
          mark.textContent = "⚠";
          pillBtn.appendChild(mark);
          pillBtn.appendChild(document.createTextNode(String(rows.length)));

          // The bubble is the body-teleported singleton (see ensureChipTip); the
          // pill only references it via aria-controls. Nothing is appended into
          // the chip except the pill.
          const tipText = m.chipTooltip || m.heading || "";
          // Create the singleton eagerly so the pill's aria-controls target
          // exists before the first hover/click.
          ensureChipTip();

          // On pointer devices hover is the primary trigger, so click just OPENS
          // (idempotent). Letting click also toggle-close would fight hover — the
          // mouseenter opens, then the same gesture's click would immediately
          // close it (a flash on hybrid touch-laptops). On touch (no hover) click
          // MUST toggle, since there's no hover to reveal/dismiss the bubble.
          pillBtn.addEventListener("click", () => {
            if (window.matchMedia("(hover: hover)").matches) {
              openChipTip(pillBtn, tipText);
              return;
            }
            const tip = ensureChipTip();
            if (tip.classList.contains("hidden")) openChipTip(pillBtn, tipText);
            else closeContrastChipTips();
          });

          // Hover open/close — DESKTOP (pointer devices) ONLY. The (hover: hover)
          // gate keeps touch devices (which synthesize a phantom hover on tap) on
          // click-only, so tap doesn't double-fire. These per-render listeners
          // can't accumulate: the pill is rebuilt on every re-render.
          if (window.matchMedia("(hover: hover)").matches) {
            pillBtn.addEventListener("mouseenter", () => openChipTip(pillBtn, tipText));
            pillBtn.addEventListener("mouseleave", () => closeContrastChipTips());
          }

          chip.appendChild(pillBtn);
          bindContrastChipDocListeners();
        }
      }

      const form = main.querySelector("#cms-editor-form") ?? main;
      let box = form.querySelector<HTMLElement>("[data-cms-contrast]");
      if (rows.length === 0) {
        // No failing pairs: remove the box entirely so no stray mt-3 node lingers.
        box?.remove();
        return;
      }
      if (!box) {
        box = document.createElement("div");
        box.setAttribute("data-cms-contrast", "");
        // No top margin: the box is inserted into CMSFormEngine's `flex
        // flex-col gap-4` column, whose gap already spaces it from the fields.
      }
      // CHANGE 2 — place the card immediately AFTER the color group, before the
      // typography fields, rather than at the form bottom. The design fields are
      // flat siblings in the CMSFormEngine `flex flex-col gap-4` container (colors
      // are fields 0-6, section_alt_color last; the first non-color field is
      // alternate_sections). Anchor on that first non-color field, climb to its
      // top-level wrapper (the direct child of the fields container — the one that
      // ALSO holds the color fields, using text_color as the landmark), and insert
      // just before it. insertBefore MOVES the single existing node, so a
      // re-render never duplicates the card. Fallback: append to the form (old
      // behavior) when the anchor is absent (e.g. a non-design section, which
      // has no color inputs and thus produces no rows anyway).
      {
        const anchor =
          main.querySelector<HTMLElement>('[data-cms-field="alternate_sections"]') ||
          main.querySelector<HTMLElement>('[data-cms-field="font_heading"]');
        let placed = false;
        if (anchor) {
          let wrapper: HTMLElement = anchor;
          let parent = wrapper.parentElement;
          while (parent && !parent.querySelector('[data-cms-field="text_color"]')) {
            wrapper = parent;
            parent = wrapper.parentElement;
          }
          if (parent) {
            parent.insertBefore(box, wrapper);
            placed = true;
          }
        }
        if (!placed && box.parentElement !== form) form.appendChild(box);
      }

      const swatch = (hex: string): HTMLElement => {
        const s = document.createElement("span");
        s.className = "inline-block w-4 h-4 rounded border border-base-content/20 shrink-0";
        s.style.background = hex;
        return s;
      };

      const card = document.createElement("div");
      card.className = "rounded-box border border-base-300 bg-base-200/40 p-3 flex flex-col gap-2";
      const heading = document.createElement("div");
      heading.className = "text-sm font-medium text-base-content";
      heading.textContent = `${m.heading || "Check contrast"} (${rows.length})`;
      card.appendChild(heading);

      for (const row of rows) {
        const line = document.createElement("div");
        line.className =
          "flex items-center gap-2 text-xs " +
          (row.severity === "error" ? "text-error" : "text-warning");
        line.appendChild(swatch(row.aHex));
        line.appendChild(swatch(row.bHex));

        const label = document.createElement("span");
        label.className = "flex-1 min-w-0";
        const pairLabel = m.pair?.[row.pair] || row.pair;
        const hint = m.hint?.[hintKeyFor(row)] || "";
        label.textContent = `${pairLabel} · ${row.ratio.toFixed(1)}:1${hint ? " · " + hint : ""}`;
        line.appendChild(label);

        // Suggested-fix swatch + Apply — ONLY for the brand pairs whose color to
        // change is unambiguously primary/accent (CTA + every brand-vs-surface
        // pair). Neutral text pairs and the surface↔background elevation pair stay
        // informational (which of two neutrals to move is ambiguous), so they
        // carry no BRAND_PAIR_FIELD entry and get no Apply.
        const field = BRAND_PAIR_FIELD[row.pair];
        if (field && row.suggestion) {
          const sug = swatch(row.suggestion);
          sug.title = m.suggestion || "Suggestion";
          line.appendChild(sug);
          const apply = document.createElement("button");
          apply.type = "button";
          apply.className = "btn btn-xs btn-ghost shrink-0";
          apply.textContent = m.apply || "Apply";
          const value = row.suggestion;
          apply.addEventListener("click", () => this._applyContrastSuggestion(field, value));
          line.appendChild(apply);
        }
        card.appendChild(line);
      }

      box.innerHTML = "";
      box.appendChild(card);
    },

    // _applyContrastSuggestion writes a suggested accessible color into a
    // ColorField (primary_color / accent_color) the SAME way an operator edit
    // does — it does NOT bypass autosave. ColorField (ui_fields.templ) binds its
    // swatch + hex text to an Alpine `hex` model and its hidden input dispatches
    // a bubbling `change` on edit; setColor(c) sets both then dispatches. We do
    // exactly that: set the Alpine model so the display updates, set the hidden
    // input's value, then dispatch the bubbling `change` the editor's delegated
    // listener turns into a normal edit → _queueEdit("site:design", …) →
    // _patchPreviewTheme (live theme-patch) + _updateContrastWarnings (re-check)
    // + the debounced draft flush. The operator's click IS the choice.
    _applyContrastSuggestion(field: string, value: string) {
      const main = this._main();
      if (!main) return;
      const hidden = main.querySelector<HTMLInputElement>(`[data-cms-field="${field}"]`);
      if (!hidden) return;
      const A = (window as { Alpine?: { $data?: (el: Element) => unknown } }).Alpine;
      const data = A?.$data?.(hidden) as { hex?: string } | undefined;
      if (data && typeof data === "object") data.hex = value; // updates swatch + hex text
      hidden.value = value;
      hidden.dispatchEvent(new Event("change", { bubbles: true }));
    },

    // _setNeutralColor sets ONE neutral ColorField exactly as an operator edit
    // does — identical mechanism to _applyContrastSuggestion (Alpine model +
    // hidden value + a bubbling `change` the editor turns into a normal
    // site:design edit). Extracted so the palette-mood apply can drive all four
    // fields through the same, single, well-worn path. Missing field → no-op.
    _setNeutralColor(field: string, value: string) {
      const main = this._main();
      if (!main) return;
      const hidden = main.querySelector<HTMLInputElement>(`[data-cms-field="${field}"]`);
      if (!hidden) return;
      const A = (window as { Alpine?: { $data?: (el: Element) => unknown } }).Alpine;
      const data = A?.$data?.(hidden) as { hex?: string } | undefined;
      if (data && typeof data === "object") data.hex = value;
      hidden.value = value;
      hidden.dispatchEvent(new Event("change", { bubbles: true }));
    },

    // _applyPaletteMood (cms-09) sets the four NEUTRAL ColorFields to a mood's
    // hexes through the ordinary set-path. Each _setNeutralColor dispatches a
    // `change` → the editor's delegated listener → _queueEdit("site:design", …) →
    // _patchPreviewTheme (live CSS-var recolor) + _updateContrastWarnings +
    // _updateActiveMood, and the 600ms autosave debounce coalesces all four into
    // ONE site:design PUT. Brand colors (primary/accent) are deliberately never
    // touched — moods are neutral ladders only.
    _applyPaletteMood(bg: string, surface: string, text: string, muted: string) {
      this._setNeutralColor("background_color", bg);
      this._setNeutralColor("surface_color", surface);
      this._setNeutralColor("text_color", text);
      this._setNeutralColor("text_muted_color", muted);
    },

    // _updateActiveMood (cms-09) re-highlights the mood whose four hexes match the
    // CURRENT neutral inputs (apply-and-forget: no stored mood — the active mood
    // is whichever one the neutrals equal). Case-insensitive compare (JSON stores
    // uppercase, ColorField values may be lowercase). Clears the highlight when
    // none matches (a hand-tweaked custom palette). No-op when the picker is
    // absent (every non-Design section). Called on load and after every
    // site:design edit, so a manual neutral change clears the highlight and a
    // mood click sets it.
    _updateActiveMood() {
      const main = this._main();
      if (!main) return;
      const buttons = main.querySelectorAll<HTMLElement>("[data-cms-mood]");
      if (buttons.length === 0) return;
      const read = (key: string): string =>
        (main.querySelector<HTMLInputElement>(`[data-cms-field="${key}"]`)?.value ?? "")
          .trim()
          .toLowerCase();
      const cur = {
        bg: read("background_color"),
        surface: read("surface_color"),
        text: read("text_color"),
        muted: read("text_muted_color"),
      };
      const norm = (v: string | undefined) => (v ?? "").trim().toLowerCase();
      buttons.forEach((btn) => {
        const match =
          norm(btn.dataset.moodBg) === cur.bg &&
          norm(btn.dataset.moodSurface) === cur.surface &&
          norm(btn.dataset.moodText) === cur.text &&
          norm(btn.dataset.moodMuted) === cur.muted;
        btn.setAttribute("aria-pressed", match ? "true" : "false");
      });
    },

    _scrollPreviewToSection(id: string) {
      const iframe = this._iframe();
      if (!iframe || !id) return;
      const sel = this._cssEscape(id);
      const el = iframe.contentDocument?.querySelector(`[data-cms-section="${sel}"]`);
      if (el) (el as HTMLElement).scrollIntoView({ behavior: "smooth", block: "start" });
      // If the section element isn't in the doc (e.g. SEO shows the snippet card,
      // not the iframe), this is a benign no-op.
    },

    // _switchSection is the rail link's @click handler. It sets the active section
    // (drives the active-row highlight) and, in FULL-PAGE preview mode, scrolls
    // the already-rendered preview to that section RIGHT NOW — so the click has
    // instant visible feedback instead of waiting on the leftpane GET. The
    // htmx:afterSettle handler still scrolls once the form pane swaps (belt and
    // suspenders: harmless re-scroll to the same target, and it covers the case
    // where the section element wasn't yet present at click time). In SECTION
    // mode the iframe reloads to the clicked section on afterSettle, so no
    // click-time scroll applies here — setting section is enough.
    _switchSection(id: string) {
      this.section = id;
      // Booking: the tab switch happens on afterSettle from the rail link's
      // URL (handleAfterSettle) — there is no section anchor to scroll to.
      if (this._previewMode() === "full-page" && !this._isBookingFullPage()) this._scrollPreviewToSection(id);
    },

    // onExternalDraftSaved runs when a draft write that BYPASSES this editor's
    // autosave flush (e.g. the seasonal envelope-image set-seasonal-image
    // route) reports success via the body-dispatched website-content-draft-saved
    // event. The normal autosave path reloads the preview itself
    // (flushPending -> _refreshPreviewReload); these external writes need the
    // same reload so the change shows without a manual page refresh.
    onExternalDraftSaved() {
      this._refreshPreviewReload();
    },

    _schedulePreview() {
      if (this._previewTimer) clearTimeout(this._previewTimer);
      this._previewTimer = setTimeout(() => this._refreshPreview(), 400);
    },
    // _previewVisible reports whether the preview iframe currently has layout
    // boxes — false when an ancestor is display:none (POI manage-mode hides
    // the whole pane via x-show, see cms_editor.templ). getClientRects() is
    // empty for a display:none subtree regardless of the element's own
    // styles, so this is a cheap, DOM-only visibility check (no getComputedStyle
    // walk needed).
    _previewVisible(): boolean {
      const f = this._iframe();
      return !!f && f.getClientRects().length > 0;
    },
    _refreshPreview() {
      // Skip the refresh entirely while the preview pane is hidden (POI
      // manage-mode): _updatePreviewInPlace would capture scrollY=0 from the
      // hidden iframe and restore to the top once the pane reappears. Mark
      // dirty instead — the reveal observer in init() re-triggers this method
      // once the iframe is visible again, so it captures/restores the RETAINED
      // scroll position instead.
      if (!this._previewVisible()) {
        this._previewDirty = true;
        return;
      }
      this._previewDirty = false;
      const mode = this._previewUpdate();
      if (mode === "theme-live") return; // theme edits live-patch CSS vars; HTML unchanged
      if (mode === "in-place") {
        void this._updatePreviewInPlace();
      } else {
        this._refreshPreviewReload();
      }
    },

    // ── consent-banner sample preview (T4) ──────────────────────────────────
    // Design-only, ephemeral show/hide + live position sync for the inert
    // production consent-banner sample the render-preview handler embeds
    // outside #cms-preview-root (handler_render_preview.go's
    // consentPreviewBlock) ONLY for Design's own &consent_sample=1 preview URL
    // (CMSSitePreviewURL). See CmsEditor.showConsentBannerPreview's doc
    // comment for the state contract.

    _consentPreviewWrap(): HTMLElement | null {
      return this._iframe()?.contentDocument?.getElementById("cms-consent-preview") ?? null;
    },
    _consentPreviewErrorNode(): HTMLElement | null {
      return this._iframe()?.contentDocument?.getElementById("cms-consent-preview-error") ?? null;
    },
    _consentPreviewToggleBtn(): HTMLButtonElement | null {
      return (
        this._main()?.querySelector<HTMLButtonElement>("[data-cms-consent-preview-toggle]") ??
        null
      );
    },
    _consentPreviewErrorBanner(): HTMLElement | null {
      return this._main()?.querySelector<HTMLElement>("[data-cms-consent-preview-error]") ?? null;
    },
    // _currentConsentBannerPosition reads the LIVE draft value straight off the
    // Design form's own consent_banner_position select — the same source
    // _patchPreviewTheme's caller (_queueEdit) already writes from on every
    // operator edit, so this always reflects any unflushed change. "bottom" is
    // the field's/theme default (matches the Go side's zero-value fallback).
    _currentConsentBannerPosition(): string {
      const main = this._main();
      const val = main?.querySelector<HTMLInputElement | HTMLSelectElement>(
        '[data-cms-field="consent_banner_position"]',
      )?.value;
      return val || "bottom";
    },
    // _applyConsentBannerPosition live-patches the sample's data-position
    // attribute + the CSS-driving modifier class on the inner #consent-banner
    // element. Safe to call whether or not the sample is currently visible —
    // keeping data-position/the class in sync at all times means a later
    // show immediately reflects the right position with no extra work.
    _applyConsentBannerPosition(position: string) {
      const wrap = this._consentPreviewWrap();
      if (!wrap) return;
      wrap.dataset.position = position;
      const banner = wrap.querySelector<HTMLElement>("#consent-banner");
      banner?.classList.toggle("consent-banner--center", position === "center");
    },
    // _syncConsentPreviewControl reflects showConsentBannerPreview + sample
    // availability onto the toggle button (label/aria-pressed/disabled) and
    // the standalone error banner placed OUTSIDE the iframe next to it
    // (cms_editor.templ's data-cms-consent-preview-error). Unavailable means
    // "no wrapper in the iframe" — covers BOTH the explicit sidecar-render-
    // failure case (the error node carries the message) AND a total page-
    // render failure (neither node exists, e.g. the sidecar itself is down —
    // see handler_render_preview.go's writeFrame(unavailable:true) branch).
    // No-op off Design (the control isn't in the DOM there).
    _syncConsentPreviewControl() {
      const btn = this._consentPreviewToggleBtn();
      if (!btn) return;
      const available = !!this._consentPreviewWrap();
      const errBanner = this._consentPreviewErrorBanner();
      if (errBanner) {
        const msg = this._consentPreviewErrorNode()?.dataset.message || "";
        errBanner.textContent = msg;
        errBanner.classList.toggle("hidden", !msg);
      }
      btn.disabled = !available;
      btn.setAttribute("aria-pressed", this.showConsentBannerPreview ? "true" : "false");
      const label = this.showConsentBannerPreview ? btn.dataset.labelHide : btn.dataset.labelShow;
      if (label) btn.textContent = label;
    },
    // toggleConsentBannerPreview is the show/hide button's click handler.
    // ONLY flips the iframe wrapper's `hidden` attribute (+ re-asserts the
    // current position) — no autosave (_queueEdit/_flush are never called),
    // no draft write, no localStorage/sessionStorage read or write (the
    // sample is a rendered fragment that never runs consent-entry.ts to begin
    // with — see handler_render_preview.go). A no-op when the sample is
    // unavailable (the button is disabled by _syncConsentPreviewControl, but
    // the delegated click listener defends too — see editor.ts).
    toggleConsentBannerPreview() {
      const wrap = this._consentPreviewWrap();
      if (!wrap) return;
      this.showConsentBannerPreview = !this.showConsentBannerPreview;
      wrap.hidden = !this.showConsentBannerPreview;
      if (this.showConsentBannerPreview) {
        this._applyConsentBannerPosition(this._currentConsentBannerPosition());
      }
      this._syncConsentPreviewControl();
    },
    // _resetConsentPreviewOnNav resets the ephemeral toggle state to hidden
    // and re-syncs the control — called from editor.ts's htmx:afterSettle
    // leftpane-swap handler on EVERY leftpane (re)render (rail nav, discard,
    // version restore, variant switch). Entering Design always starts hidden;
    // leaving Design clears state so a later return doesn't resurrect a stale
    // show. A no-op off Design (_syncConsentPreviewControl bails with no
    // toggle button in the freshly-rendered DOM).
    //
    // Review fix (round 1): this used to rely ENTIRELY on the iframe reload
    // that (normally) accompanies a leftpane swap to re-hide the sample —
    // _onPreviewLoad recreates #cms-consent-preview hidden by default. But two
    // real leftpane-swap paths in editor.ts's afterSettle handler do NOT
    // reload the iframe:
    //   1. Re-clicking Design while already on Design — editor.ts's urlStore
    //      write is identity-equal (same URL), so the iframe :src never
    //      re-evaluates and no `load` fires.
    //   2. Design → a "card surface" section (e.g. SEO,
    //      data-cms-preview-surface="card") → back to Design — card surfaces
    //      deliberately leave the iframe on its last real content render
    //      (editor.ts's isCardSurface branch), so the iframe is STILL showing
    //      Design's old render throughout; the return trip is then ALSO an
    //      identity-equal write (same URL as before the detour) → no reload.
    // In both cases the OLD #cms-consent-preview node survives untouched, so
    // resetting only the client flag left the sample visibly shown while the
    // button read "Show"/aria-pressed="false". Hide the wrapper directly here
    // too — correct whether or not a reload is coming: if one IS coming, this
    // touches the (about-to-be-replaced) old iframe document harmlessly and
    // _onPreviewLoad reasserts the same hidden state once the new one lands.
    _resetConsentPreviewOnNav() {
      this.showConsentBannerPreview = false;
      const wrap = this._consentPreviewWrap();
      if (wrap) wrap.hidden = true;
      this._syncConsentPreviewControl();
    },
  };
}
